import { useCallback, useMemo, useState } from 'react';

import { Add, Search } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { InfiniteData } from 'react-query/types/core/types';
import {
  FidcComparatorFundOption,
  FidcComparatorOption,
  getFidcComparatorFundOptions,
} from 'src/services/fidcComparator';
import { StrapiResponse } from 'src/types/http';
import { debounce } from 'src/utils/general';

import { MAX_SELECTED_FIDCS } from '../constants';
import { formatSelectedFidcLabel } from '../selectionUtils';

const PAGE_SIZE = 30;
const FUND_OPTIONS_QUERY_KEY = 'fidc-comparator-fund-options';

const formatFundSearchLabel = ({
  name,
  cnpj,
}: {
  name?: string | null;
  cnpj?: string | null;
}) => [name?.trim(), cnpj?.trim()].filter(Boolean).join(' ');

interface FidcSelectorProps {
  values: FidcComparatorOption[];
  onChange: (options: FidcComparatorOption[]) => void;
  isComparatorLoading?: boolean;
}

export default function FidcSelector({
  values,
  onChange,
  isComparatorLoading = false,
}: FidcSelectorProps) {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [inputFocused, setInputFocused] = useState(false);
  const [selectedFund, setSelectedFund] =
    useState<FidcComparatorFundOption | null>(null);
  const debouncedSetSearch = useCallback(
    debounce((val: string) => {
      setSearch(val);
    }, 400),
    [],
  );

  const resetFundOptionsCacheToFirstPage = useCallback(
    (searchTerm: string) => {
      queryClient.setQueryData<
        InfiniteData<StrapiResponse<FidcComparatorFundOption[]>> | undefined
      >(
        [FUND_OPTIONS_QUERY_KEY, searchTerm],
        (
          oldData:
            | InfiniteData<StrapiResponse<FidcComparatorFundOption[]>>
            | undefined,
        ) => {
          if (!oldData?.pages?.length) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.slice(0, 1),
            pageParams: oldData.pageParams?.slice(0, 1) ?? [],
          };
        },
      );
    },
    [queryClient],
  );

  const {
    data: fundsData,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingFunds,
    isFetching: isFetchingFunds,
  } = useInfiniteQuery({
    queryKey: [FUND_OPTIONS_QUERY_KEY, search],
    queryFn: ({ pageParam = 1 }) =>
      getFidcComparatorFundOptions(search, pageParam, PAGE_SIZE),
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.pagination.page < lastPage.meta.pagination.pageCount) {
        return lastPage.meta.pagination.page + 1;
      }

      return undefined;
    },
    enabled: inputFocused || search.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5,
  });

  const isLoadingFundsOrFetching = useMemo(
    () => isLoadingFunds || isFetchingFunds,
    [isLoadingFunds, isFetchingFunds],
  );

  const fundOptions = useMemo(
    () => fundsData?.pages.flatMap((page) => page.data ?? []) ?? [],
    [fundsData],
  );

  const canAdd =
    Boolean(selectedFund) &&
    values.length < MAX_SELECTED_FIDCS &&
    !isComparatorLoading &&
    !isLoadingFundsOrFetching;

  const selectedValues = useMemo(
    () => new Set(values.map((item) => item.value)),
    [values],
  );

  const handleAdd = () => {
    if (!selectedFund) return;

    const value = String(selectedFund.operationId);
    if (selectedValues.has(value)) return;

    onChange([
      ...values,
      {
        value,
        operationId: selectedFund.operationId,
        name: selectedFund.name,
        cnpj: selectedFund.cnpj,
        cota: null,
        serie: null,
        label: formatSelectedFidcLabel({
          name: selectedFund.name,
          cota: null,
          serie: null,
        }),
      },
    ]);

    setSelectedFund(null);

    resetFundOptionsCacheToFirstPage('');
    setSearch('');
  };

  const handleDelete = (value: string) => {
    onChange(values.filter((item) => item.value !== value));
  };

  const handleFundsScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const listbox = event.currentTarget;
    const reachedBottom =
      listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 1;

    if (reachedBottom && hasNextPage && !isFetchingFunds) {
      fetchNextPage();
    }
  };

  return (
    <Stack gap={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        gap={2}
        alignItems={{ xs: 'stretch', md: 'flex-start' }}>
        <Autocomplete
          fullWidth
          blurOnSelect
          popupIcon={null}
          value={selectedFund}
          noOptionsText="Nenhum FIDC encontrado"
          loading={isLoadingFunds}
          loadingText="Carregando..."
          options={fundOptions}
          ListboxProps={{ onScroll: handleFundsScroll }}
          filterOptions={(items) => items}
          isOptionEqualToValue={(option, val) =>
            option.operationId === val.operationId
          }
          getOptionLabel={(option) =>
            formatFundSearchLabel(option) || option.label
          }
          onChange={(_, selected) => {
            setSelectedFund(selected as FidcComparatorFundOption | null);
          }}
          onInputChange={(_, val, reason) => {
            if (reason === 'input') {
              debouncedSetSearch(val);
            }

            if (reason === 'clear') {
              resetFundOptionsCacheToFirstPage('');
              setSearch('');
            }
          }}
          renderOption={(props, option) => (
            <Box
              {...props}
              component="li"
              sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="secondary.main">
                {option.name?.trim() || option.label}
              </Typography>
              {option.cnpj && (
                <Typography variant="caption" color="text.secondary">
                  {option.cnpj.trim()}
                </Typography>
              )}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="FIDC"
              helperText="Busque FIDCs por nome ou CNPJ"
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Button
          variant="contained"
          startIcon={<Add />}
          disabled={!canAdd}
          onClick={handleAdd}
          sx={{
            minWidth: { md: 220 },
            height: 40,
            mt: 0,
            alignSelf: { xs: 'stretch', md: 'flex-start' },
            whiteSpace: 'nowrap',
          }}>
          Adicionar fundo
        </Button>
      </Stack>

      <Box>
        <Stack direction="row" alignItems="center" gap={1} mb={1}>
          <Typography variant="caption" color="text.secondary">
            FIDCs selecionados ({values.length}/{MAX_SELECTED_FIDCS})
          </Typography>
          <Box
            sx={{
              height: 1,
              flex: 1,
              bgcolor: theme.palette.divider,
            }}
          />
        </Stack>

        <Stack direction="row" gap={1} flexWrap="wrap">
          {values.map((option, index) => (
            <Chip
              key={option.value}
              label={formatSelectedFidcLabel(option)}
              onDelete={() => handleDelete(option.value)}
              variant="outlined"
              sx={{
                borderRadius: 1,
                bgcolor: theme.palette.background.paper,
                borderColor: theme.palette.divider,
                '& .MuiChip-label': {
                  fontWeight: 600,
                },
                '&::before': {
                  content: `'${index + 1}'`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  ml: 1,
                  borderRadius: '50%',
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  fontSize: 12,
                  fontWeight: 700,
                },
              }}
            />
          ))}
          {values.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Nenhum FIDC selecionado. Use os campos acima para montar a
              comparação.
            </Typography>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}
